<template>
	<div class="editor-wrapper">
		<editor-menu-bar
			:editor="editor"
			v-if="extensions.length"
			v-slot="{ commands, isActive, getMarkAttrs }"
		>
			<div class="menubar">
				<app-button
					v-for="(extension, id) in extensions"
					:key="`menubar-button-${id}`"
					:class="{
						'menubar__button': true,
						'annotation': !!extension.annotation,
						'primary--text': isActive[extension.command](extension.args || {})
					}"
					:style="extensionCSSVars(extension)"
					:icon="extension.icon"
					icon-only
					small
					flat
					no-margin
					@click="execCommand(commands, getMarkAttrs, extension.command, extension.args || {})"
				/>
			</div>
		</editor-menu-bar>
		<editor-content
			class="editor__content"
			:editor="editor"
		/>
		<app-dialog v-model="dialogs.link.show" max-width="320" title="Manage link">
			<app-dialog-body>
				<input-text
					v-model="dialogs.link.value"
					label="Link"
					clearable
					placeholder="https://example.com"
				/>
			</app-dialog-body>
			<app-dialog-footer>
				<app-button
					color="primary"
					@click="updateLink"
				>Save</app-button>
				<app-button @click="dialogs.link.show = false">Cancel</app-button>
			</app-dialog-footer>
		</app-dialog>
	</div>
</template>

<script>
import { map } from 'lodash'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
	Bold,
	Italic,
	Underline,
	Strike,
	BulletList,
	OrderedList,
	ListItem,
	Link,
	Heading,
	HardBreak,
	HorizontalRule,
} from 'tiptap-extensions'

const extensions = {
	bold: {
		tiptap: Bold,
		command: 'bold',
		icon: 'fa-bold',
	},
	italic: {
		tiptap: Italic,
		command: 'italic',
		icon: 'fa-italic',
	},
	underline: {
		tiptap: Underline,
		command: 'underline',
		icon: 'fa-underline',
	},
	strike: {
		tiptap: Strike,
		command: 'strike',
		icon: 'fa-strikethrough',
	},
	bullet: {
		tiptap: BulletList,
		command: 'bullet_list',
		icon: 'fa-list',
	},
	ordered: {
		tiptap: OrderedList,
		command: 'ordered_list',
		icon: 'fa-list-ol',
	},
	link: {
		tiptap: Link,
		tiptapArgs: [{ openOnClick: false }],
		command: 'link',
		icon: 'fa-link',
	},
	heading3: {
		tiptap: Heading,
		annotation: '3',
		command: 'heading',
		icon: 'fa-heading',
		args: { level: 3 },
	},
	heading4: {
		tiptap: Heading,
		annotation: '4',
		command: 'heading',
		icon: 'fa-heading',
		args: { level: 4 },
	},
	br: {
		tiptap: HardBreak,
		command: 'hard_break',
		icon: 'fa-level-down-alt',
	},
	hr: {
		tiptap: HorizontalRule,
		command: 'horizontal_rule',
		icon: 'fa-grip-lines',
	},
}

export default {
	components: {
		EditorContent,
		EditorMenuBar,
	},
	props: {
		autoFocus: {
			type: Boolean,
			default: false,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		value: {
			type: [Object, String],
			default: '',
		},
		json: {
			type: Boolean,
			default: false,
		},
		supportedExtensions: {
			type: Array,
			default: () => Object.keys(extensions),
			validate(values) {
				const supported = Object.keys(extensions)
				return values.every(value => supported.includes(value))
			},
		},
	},
	data() {
		return {
			editor: null,
			dialogs: {
				link: {
					show: false,
					commands: null,
					value: null,
					oriValue: null,
				},
			},
		}
	},
	watch: {
		value(value) {
			if (value !== this.editorValue) {
				this.editor.setContent(value)
			}
		},
	},
	computed: {
		content() {
			return this.value
		},
		extensions() {
			return this.supportedExtensions.map(value => extensions[value])
		},
		editorValue() {
			if (this.json) {
				return this.editor.getJSON()
			}
			return this.editor.getHTML()
		},
	},
	mounted() {
		this.editor = new Editor({
			extensions: this.newExtensions(),
			content: this.content,
			autoFocus: this.autoFocus,
			editable: !this.readonly,
			onBlur: this.onBlur.bind(this),
		})
	},
	methods: {
		newExtensions() {
			const newExtensions = map(
				this.extensions,
				({ tiptap: TiptapExtension, tiptapArgs }) => new TiptapExtension(...(tiptapArgs || [])),
			)

			if (this.supportedExtensions.some(extension => ['bullet', 'ordered'].includes(extension))) {
				newExtensions.push(new ListItem())
			}
			return newExtensions
		},
		getExtension(value) {
			return extensions[value]
		},
		onBlur() {
			this.$emit('input', this.editorValue)
		},
		openLinkDialog(commands, attrs) {
			this.dialogs.link.commands = commands
			this.dialogs.link.value = attrs.href
			this.dialogs.link.oriValue = attrs.href
			this.dialogs.link.show = true
		},
		updateLink() {
			this.dialogs.link.commands.link({ href: this.dialogs.link.value })
			this.dialogs.link.show = false
		},
		execCommand(commands, getMarkAttrs, name, args = {}) {
			switch (name) {
			case 'link':
				this.openLinkDialog(commands, getMarkAttrs('link'))
				break
			default:
				commands[name](args)
			}
		},
		extensionCSSVars({ annotation }) {
			if (!annotation) { return undefined }
			return { '--annotation': `"${annotation}"` }
		},
	},
	beforeDestroy() {
		if (this.editor) {
			this.editor.destroy()
		}
	},
}
</script>

<style scoped>
	.menubar {
		background: #f1f1f1;
		font-size: 12px;
		position: sticky;
		top: 0;
		z-index: 10;
	}
	.menubar .menubar__button {
		min-width: 28px;
	}

	.editor__content {
		border: 1px solid #f1f1f1;
		padding: 4px;
	}

	.editor__content >>> .ProseMirror {
		outline: none;
	}

	.editor__content p {
		margin-bottom: 0;
	}

	.menubar .menubar__button.annotation::after {
		content: var(--annotation);
		position: absolute;
		bottom: -5px;
		right: 4px;
	}
</style>
